import { useState, useMemo } from "react";
import { Select, Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomPagination from "../../components/pagination";
import StatusTabs from "../../components/statusTabs";
import SearchBar from "../../components/searchBar";
import ColumnVisibility from "../../components/columnVisibility";
import ChangePasswordModal from "../../components/modals/ChangePasswordModal";
import { getUserColumns, MembershipUserRecord, SalesPerson } from "./columns";
import "./styles.scss";
import { useGetAllUserQuery, useGetUsersByRoleQuery, useUpdateCanTransferMutation } from "../../services/user";
import { useCountries } from "../../hooks/useCountries";
import { useUpdateSalesPersonMutation } from "../../services/invoice";
import CommonTable from "../../components/commonTable";
import usePermissions from "../../hooks/usePermissions";
import { formatDate } from "../../utils/date";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface VisibleColumns {
  name:            boolean;
  branch:          boolean;
  phoneNumber:     boolean;
  assessmentRatio: boolean;
  salesPerson:     boolean;
  planName:        boolean;
  addon:           boolean;
  planPrice:       boolean;
  profile:         boolean;
  gender:          boolean;
  status:          boolean;
  invoiceStatus:   boolean;
  remainingDays:   boolean;
  membershipForm:  boolean;
  startDate:       boolean;
  endDate:         boolean;
  gymKit:          boolean;
  actions:         boolean;
}

const remainingDaysOptions = [
  { value: 'all', label: 'All R.Days' },
  { value: '7',   label: '7 Days'             },
  { value: '15',  label: '15 Days'            },
  { value: '30',  label: '30 Days'            },
];

const verifiedOptions = [
  { value: 'all',   label: 'All Users' },
  { value: 'true',  label: 'Verified'  },
  { value: 'false', label: 'Unverified' },
];

const saleTypeOptions = [
  { value: 'all',            label: 'All Sale Types' },
  { value: 'new_client',     label: 'New Client'     },
  { value: 'renew',          label: 'Renew'          },
  { value: 'club_transfer',  label: 'Club Transfer'  },
  { value: 'upgrade',        label: 'Upgrade'        },
  { value: 'balance_clear',  label: 'Balance Clear'  },
  { value: 'transfer_fee',   label: 'Transfer Fee'   },
  { value: 'locker',         label: 'Locker'         },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AllMembershipUsers = () => {
  const { hasPermission } = usePermissions();
  const branchId         = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const [page,                setPage]                = useState(1);
  const [limit,               setLimit]               = useState(10);
  const [searchText,          setSearchText]          = useState("");
  const [activeTab,           setActiveTab]           = useState("all");
  const [remainingDays,       setRemainingDays]       = useState("all");
  const [verifiedFilter,      setVerifiedFilter]      = useState("all");
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<string[]>([]);
  const [saleTypeFilter,      setSaleTypeFilter]      = useState("all");
  const [showPasswordModal,   setShowPasswordModal]   = useState(false);
  const [selectedUser,        setSelectedUser]        = useState<MembershipUserRecord | null>(null);

  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>({
    name:            true,
    branch:          true,
    phoneNumber:     true,
    assessmentRatio: true,
    salesPerson:     true,
    planName:        true,
    addon:           true,
    planPrice:       true,
    profile:         true,
    gender:          true,
    status:          true,
    invoiceStatus:   true,
    remainingDays:   true,
    membershipForm:  true,
    startDate:       true,
    endDate:         true,
    gymKit:          true,
    actions:         true,
  });

  const navigate = useNavigate();
  const { dialCodeMap } = useCountries();

  const handleEdit = (_record: MembershipUserRecord) => {
    // navigation handled inside columns via EditUserRoute
  };

  const handleDelete = (_record: MembershipUserRecord) => {
    // TODO: confirmation modal + delete API
  };

  const handleChangePassword = (record: MembershipUserRecord) => {
    setSelectedUser(record);
    setShowPasswordModal(true);
  };

  const handlePasswordModalCancel = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
  };

  const { data, isLoading, refetch } = useGetAllUserQuery({
    page,
    limit,
    search:         searchText,
    status:         activeTab,
    branchIds:      selectedBranchId || undefined,
    membershipType: 'regular',
    salesPersonId:  selectedSalesPerson,
    remainingDaysMax: remainingDays !== 'all' ? remainingDays : undefined,
    verified:         verifiedFilter !== 'all' ? verifiedFilter : undefined,
    saleType:         saleTypeFilter !== 'all' ? saleTypeFilter : undefined,
  } as any);

  const { data: salesPersonsData } = useGetUsersByRoleQuery({
    role:     'sales_representative',
    branchId: selectedBranchId || undefined,
  } as any);

  const [updateSalesPerson] = useUpdateSalesPersonMutation();
  const [updateCanTransfer] = useUpdateCanTransferMutation();

  const allSalesPersons = useMemo<SalesPerson[]>(() => {
    const salesPersonMap = new Map<string, SalesPerson>();
    const apiSalesPersons: any[] = (salesPersonsData as any)?.data || [];
    apiSalesPersons.forEach((sp: any) => {
      if (sp._id && sp.name) salesPersonMap.set(sp._id, { _id: sp._id, name: sp.name, phoneNumber: sp.phoneNumber });
    });
    const users: any[] = (data as any)?.users || [];
    users.forEach((user: any) => {
      if (Array.isArray(user.salesPerson)) {
        user.salesPerson.forEach((sp: any) => {
          if (sp._id && sp.name && !salesPersonMap.has(sp._id)) {
            salesPersonMap.set(sp._id, { _id: sp._id, name: sp.name, phoneNumber: sp.phoneNumber });
          }
        });
      }
    });
    return Array.from(salesPersonMap.values());
  }, [salesPersonsData, data]);

  const handleSalesPersonChange = async (userId: string | undefined, salesPersonId: string) => {
    try {
      await (updateSalesPerson as any)({ userId, salesPersonId }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to update sales person:', err);
    }
  };

  const handleMembershipTransfer = (record: MembershipUserRecord, canTransfer: boolean) => {
    if (!record._id) return;
    Modal.confirm({
      title:   canTransfer ? 'Enable Membership Transfer' : 'Disable Membership Transfer',
      content: (
        <p>
          Are you sure you want to {canTransfer ? 'enable' : 'disable'} membership transfer for{' '}
          <strong>{record.name}</strong>?
        </p>
      ),
      okText:  'Confirm',
      okType:  canTransfer ? 'primary' : 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await updateCanTransfer({ id: record._id as string, canTransfer }).unwrap();
          refetch();
        } catch {
          message.error('Failed to update membership transfer status');
        }
      },
    });
  };

  const columnsWithHandlers = getUserColumns(
    handleEdit,
    handleDelete,
    handleChangePassword,
    navigate,
    allSalesPersons,
    handleSalesPersonChange,
    hasPermission,
    dialCodeMap,
    handleMembershipTransfer,
  );

  const usersData = useMemo<MembershipUserRecord[]>(() => {
    const users: any[] = (data as any)?.users;
    if (!users) return [];
    return users.map((user: any): MembershipUserRecord => {
      const member            = user.member             || {};
      const currentMembership = user.currentMembership  || {};
      const salesPersonArray: any[] = user.salesPerson  || [];
      const salesPerson       = salesPersonArray[0]     ?? null;
      const previousSalesPersons = salesPersonArray.slice(1).map((sp: any) => sp.name).join(', ');
      return {
        _id:             user._id,
        name:            user.name,
        branch:          user.branchIds?.length > 0 ? user.branchIds[0]?.name : '-',
        phoneNumber:     user.phoneNumber,
        countryCode:     member.countryCode || '',
        assessmentRatio: '-',
        salesPerson:     previousSalesPersons || '-',
        salesPersonId:   salesPerson?._id     || null,
        planName:        currentMembership.planName  || 'N/A',
        addon:           user.addonMembership?.planName || '-',
        planPrice:       currentMembership.pricing   || '-',
        profile:         member.photo,
        gender:          member.gender || '-',
        status:          user.status,
        transferStatus:  user.transferStatus || null,
        invoiceStatus:   currentMembership.invoiceStatus
          ? { ...currentMembership.invoiceStatus, saleType: currentMembership.saleType }
          : null,
        remainingDays:   currentMembership.totalDays && currentMembership.remainingDays !== undefined
          ? `${currentMembership.totalDays}/${currentMembership.remainingDays}` : '-',
        membershipForm:  '-',
        startDate:       formatDate(currentMembership.startDate),
        endDate:         formatDate(currentMembership.expiryDate),
        gymKit:          '-',
        planGymKit:      user.planGymKit,
        deliveredSummary: user.deliveredSummary,
        isVerified:      !!user.isVerified,
        verifiedByName:  user.verifiedBy?.name || null,
        canTransfer:     member.canTransfer,
      };
    });
  }, [data]);

  const apiData = data as any;
  const tabsData = [
    { key: 'all',      label: 'All',      count: apiData?.statusCounts?.all      || 0 },
    { key: 'active',   label: 'Active',   count: apiData?.statusCounts?.active   || 0 },
    { key: 'inactive', label: 'Inactive', count: apiData?.statusCounts?.inactive || 0 },
    { key: 'freezed',  label: 'Freezed',  count: apiData?.statusCounts?.freezed  || 0 },
    // { key: 'blocked',  label: 'Block',    count: apiData?.statusCounts?.blocked  || 0 },
    { key: 'advance',  label: 'Advance',  count: apiData?.statusCounts?.advance  || 0 },
  ];

  const salesPersonOptions = allSalesPersons.map(sp => ({ value: sp._id, label: sp.name }));

  const columns = columnsWithHandlers.filter(col => visibleColumns[col.key as keyof VisibleColumns]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey as keyof VisibleColumns] }));
  };

  return (
    <div className="all-membership-users-page">
      <div className="header-section">
        <div className="left-col">
          <SearchBar
            value={searchText}
            onChange={(val: string) => { setSearchText(val); setPage(1); }}
            placeholder="Search membership users..."
          />
          <StatusTabs
            activeTab={activeTab}
            onTabChange={(tab: string) => { setActiveTab(tab); setPage(1); }}
            tabs={tabsData}
          />
        </div>
        <div className="right-col">
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            value={selectedSalesPerson}
            onChange={(vals: string[]) => { setSelectedSalesPerson(vals); setPage(1); }}
            className="remaining-days-filter"
            style={{ width: 140, height: 41 }}
            showSearch
            optionFilterProp="label"
            placeholder="Sales Representative"
            options={salesPersonOptions}
          />
          <Select
            value={remainingDays}
            onChange={(val: string) => { setRemainingDays(val); setPage(1); }}
            className="remaining-days-filter"
            style={{ width: 130, height: 41 }}
            options={remainingDaysOptions}
          />
          <Select
            value={verifiedFilter}
            onChange={(val: string) => { setVerifiedFilter(val); setPage(1); }}
            className="remaining-days-filter"
            style={{ width: 110, height: 41 }}
            options={verifiedOptions}
          />
          <Select
            value={saleTypeFilter}
            onChange={(val: string) => { setSaleTypeFilter(val); setPage(1); }}
            className="remaining-days-filter"
            style={{ width: 140, height: 41 }}
            options={saleTypeOptions}
          />
          <ColumnVisibility
            columns={columnsWithHandlers}
            visibleColumns={visibleColumns}
            onColumnToggle={handleColumnToggle}
          />
        </div>
      </div>

      <div className="users-table-wrapper">
        <CommonTable
          columns={columns}
          dataSource={usersData}
          loading={isLoading}
          pagination={false}
          rowKey={(record: MembershipUserRecord) => record._id || record.id || ''}
          scroll={{ x: 1800 }}
        />
      </div>

      <CustomPagination
        current={page}
        pageSize={limit}
        total={apiData?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />

      <ChangePasswordModal
        visible={showPasswordModal}
        onCancel={handlePasswordModalCancel}
        selectedUser={selectedUser}
        userType="user"
      />
    </div>
  );
};

export default AllMembershipUsers;
